import { isJSON } from "./index";
import { PageType, type SiteData } from "../types";
import { COOKIE } from "./cookie";

export function canViewable(payload: {
  pageType: PageType;
  siteData: SiteData;
  cookies: Record<COOKIE, any>;
}) {
  const { pageType, siteData, cookies } = payload;
  const availablePages = siteData?.availablePages || cookies[COOKIE.AVAILABLE_PAGES] || [] as string[];
  const isPreview = !!siteData?.preview || !!cookies[COOKIE.IS_PREVIEW];

  if (isPreview && !siteData?.sandbox) return true;

  const visitedPages = cookies[COOKIE.VISITED] as string[] || [];
  const bypassRegister = !!siteData?.bypassRegister || !!cookies[COOKIE.BYPASS_REGISTER];
  const isUserAlreadySubscribed = !!cookies[COOKIE.USER_ALREADY_SUBSCRIBED];

  function isPageAvailable(page: PageType) {
    return availablePages?.includes(page);
  }

  function isPageVisited(page: PageType) {
    return visitedPages?.includes(page);
  }

  switch (pageType) {
    case PageType.LANDING:
      return availablePages?.includes(PageType.LANDING);
    case PageType.QUIZ: {
      const isAvailableQuiz = isPageAvailable(PageType.QUIZ);
      const isAvailableLanding = isPageAvailable(PageType.LANDING);

      if (isAvailableQuiz && isAvailableLanding) {
        return isPageVisited(PageType.LANDING);
      }
      return isAvailableQuiz;
    }
    case PageType.REGISTER: {
      if (bypassRegister) return false;

      const isAvailableRegister = isPageAvailable(PageType.REGISTER);
      if (isAvailableRegister) {
        if (isPageAvailable(PageType.QUIZ)) {
          return isPageVisited(PageType.QUIZ);
        } else if (isPageAvailable(PageType.LANDING)) {
          return isPageVisited(PageType.LANDING);
        }
      }

      return isAvailableRegister;
    }
    case PageType.PAYWALL: {
      const isAvailablePaywall = isPageAvailable(PageType.PAYWALL);
      if (!isAvailablePaywall) return false;

      if (!bypassRegister && isPageAvailable(PageType.REGISTER)) {
        return isPageVisited(PageType.REGISTER);
      } else if (isPageAvailable(PageType.QUIZ)) {
        return isPageVisited(PageType.QUIZ);
      } else if (isPageAvailable(PageType.LANDING)) {
        return isPageVisited(PageType.LANDING);
      }

      return true;
    }
    case PageType.PAYMENT: {
      const isAvailablePayment = isPageAvailable(PageType.PAYMENT);
      if (!isAvailablePayment) return false;

      if (isPageAvailable(PageType.PAYWALL)) {
        return isPageVisited(PageType.PAYWALL);
      } else if (!bypassRegister && isPageAvailable(PageType.REGISTER)) {
        return isPageVisited(PageType.REGISTER);
      } else if (isPageAvailable(PageType.QUIZ)) {
        return isPageVisited(PageType.QUIZ);
      } else if (isPageAvailable(PageType.LANDING)) {
        return isPageVisited(PageType.LANDING);
      }

      return true;
    }
    case PageType.PAYMENT_SUCCESS: {
      if (isUserAlreadySubscribed) return true;
      return isPageVisited(PageType.PAYMENT);
    }
  }

  return true;
}

export function getFirstPage(siteData: SiteData, cookies?: Record<COOKIE, any>) {
  const bypassRegister = !!siteData?.bypassRegister || !!cookies?.[COOKIE.BYPASS_REGISTER];
  const availablePages = siteData?.availablePages || cookies?.[COOKIE.AVAILABLE_PAGES] as string[];
  let page = availablePages?.[0] as PageType || PageType.LANDING;

  if (page === PageType.REGISTER && bypassRegister) {
    page = availablePages?.[1] as PageType;
  }

  return page;
}

export function parseValue(value: any) {
  if (value === 'true') return true;
  else if (value === 'false') return false;
  else if (value === undefined) return undefined;
  else if (value === null) return null;
  else if (isJSON(value)) return JSON.parse(value);
  else if (!isNaN(Number(value))) return +value;
  return value;
}
